import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const PaginatedList = ({ data, title, renderItem, emptyMessage }) => {
    const [displayCount, setDisplayCount] = useState(15);
    const displayedData = data.slice(0, displayCount);

    // Reset display count if data changes
    useEffect(() => {
        setDisplayCount(15);
    }, [data]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '15px' }}>{title}</h3>
            {data.length === 0 ? (
                <p style={{ color: '#6b7280' }}>{emptyMessage}</p>
            ) : (
                displayedData.map((item, index) => renderItem(item, index))
            )}
            
            {data.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                        Showing {displayedData.length} of {data.length} records
                    </span>
                    {displayCount < data.length && (
                        <button 
                            onClick={() => setDisplayCount(prev => prev + 15)}
                            style={{ 
                                padding: '8px 16px', 
                                backgroundColor: '#f1f5f9', 
                                color: '#334155',
                                border: '1px solid #e2e8f0', 
                                borderRadius: '6px', 
                                cursor: 'pointer', 
                                fontWeight: '600',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                        >
                            Load More
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default function EmployeeRecordsModule({ employee, serverId, canEdit }) {
    const [activeSubTab, setActiveSubTab] = useState('auditRecords');
    const [loading, setLoading] = useState(true);

    const [auditRecords, setAuditRecords] = useState([]);
    const [previousVisits, setPreviousVisits] = useState([]);
    const [feedbackRecords, setFeedbackRecords] = useState([]);
    const [incidentForms, setIncidentForms] = useState([]);

    useEffect(() => {
        if (!employee || !serverId) return;
        fetchAllRecordsData();
    }, [employee, serverId]);

    const fetchAllRecordsData = async () => {
        setLoading(true);
        try {
            const auditRef = collection(db, 'servers', serverId, 'auditRecords');
            const q1 = query(auditRef, where('staffID', '==', employee.username));
            const q2 = query(auditRef, where('employeeId', '==', employee.username));
            
            const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
            const auditsMap = new Map();
            snap1.forEach(doc => auditsMap.set(doc.id, { id: doc.id, ...doc.data() }));
            snap2.forEach(doc => auditsMap.set(doc.id, { id: doc.id, ...doc.data() }));
            
            let audits = Array.from(auditsMap.values());
            audits.sort((a, b) => {
                const dateA = a.auditedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.auditedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
            setAuditRecords(audits);

            const visitsRef = collection(db, 'servers', serverId, 'allVisits');
            const visitsQuery = query(visitsRef, where('staffIDs', 'array-contains', employee.username));
            const visitsSnap = await getDocs(visitsQuery);
            let visits = [];
            visitsSnap.forEach(doc => {
                visits.push({ id: doc.id, ...doc.data() });
            });
            // Order by datetime locally since orderBy requires a composite index with array-contains
            visits.sort((a, b) => {
                const dateA = a.datetime?.toDate?.() || new Date(0);
                const dateB = b.datetime?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
            setPreviousVisits(visits);

            const feedbackRef = collection(db, 'servers', serverId, 'employees', employee.username, 'feedbackRecords');
            const feedbackSnap = await getDocs(feedbackRef);
            let feedbacks = [];
            feedbackSnap.forEach(doc => {
                feedbacks.push({ id: doc.id, ...doc.data() });
            });
            feedbacks.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
            setFeedbackRecords(feedbacks);

            const incidentRef = collection(db, 'servers', serverId, 'incidentForms');
            const incidentQ = query(incidentRef, where('postedFor', '==', employee.username));
            const incidentSnap = await getDocs(incidentQ);
            let incidents = [];
            incidentSnap.forEach(doc => {
                incidents.push({ id: doc.id, ...doc.data() });
            });
            incidents.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
            setIncidentForms(incidents);

        } catch (error) {
            console.error("Error fetching records data: ", error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'completed' || s === 'closed' || s === 'approved') return '#10B981';
        if (s === 'missed' || s === 'cancelled' || s === 'rejected') return '#EF4444';
        if (s === 'pending' || s === 'open') return '#F59E0B';
        return '#6B7280';
    };

    const renderAuditRecords = () => {
        let open = 0, completed = 0, actions = 0, concerns = 0;
        auditRecords.forEach(a => {
            const status = (a.status || 'Open').toLowerCase();
            const outcome = (a.outcome || '').toLowerCase();
            if (status === 'completed' || status === 'closed') completed++;
            else open++;
            if (a.actionsRequired || a.actionRequired) actions++;
            if (outcome.includes('concern') || outcome.includes('improvement')) concerns++;
        });

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Metrics Card */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <span style={{ fontSize: '24px' }}>📋</span>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>Employee HR Audit Register</h3>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
                        Record and evidence HR-related audits, spot checks, supervisions, risk-review checks, conduct/capability reviews and visit-linked quality audits.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total', value: auditRecords.length, color: '#3B82F6' },
                            { label: 'Open', value: open, color: '#F59E0B' },
                            { label: 'Completed', value: completed, color: '#10B981' },
                            { label: 'Actions', value: actions, color: '#8B5CF6' },
                            { label: 'Concerns', value: concerns, color: '#EF4444' }
                        ].map(metric => (
                            <div key={metric.label} style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 16px', minWidth: '120px' }}>
                                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>{metric.label}</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: metric.color }}>{metric.value}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Audit List */}
                <PaginatedList
                    title="Audit History"
                    data={auditRecords}
                    emptyMessage="No audit records found."
                    renderItem={(audit) => {
                        const date = audit.auditedAt?.toDate?.() || audit.createdAt?.toDate?.();
                        const formattedDate = date ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date) : 'N/A';
                        
                        return (
                            <div key={audit.id} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                                {audit.category || audit.auditCategory || 'General Audit'}
                                            </h4>
                                            <span style={{ backgroundColor: getStatusColor(audit.status) + '20', color: getStatusColor(audit.status), padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600' }}>
                                                {audit.status || 'Open'}
                                            </span>
                                        </div>
                                        <div style={{ color: '#6b7280', fontSize: '13px' }}>
                                            📅 {formattedDate} {audit.auditor ? `• Audited by ${audit.auditor}` : ''}
                                        </div>
                                    </div>
                                </div>
                                
                                {(audit.note || audit.findings) && (
                                    <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#4b5563' }}>
                                        <strong>Findings:</strong> {audit.findings || audit.note}
                                    </div>
                                )}
                                
                                {(audit.actionsRequired || audit.actionRequired) && (
                                    <div style={{ backgroundColor: '#fef2f2', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#b91c1c' }}>
                                        <strong>Actions Required:</strong> {audit.actionsRequired || 'Yes'}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
            </div>
        );
    };

    const renderPreviousVisits = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <PaginatedList
                    title="Previous Visits History"
                    data={previousVisits}
                    emptyMessage="No previous visits found."
                    renderItem={(v) => {
                        const start = v.datetime?.toDate?.();
                        const end = v.datetimeEnd?.toDate?.();
                        const timeString = start ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(start) + (end ? ' - ' + new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(end) : '') : 'Time not set';
                        const dateString = start ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(start) : '';
                        
                        let addressStr = 'Address not provided';
                        if (v.address) {
                            if (typeof v.address === 'string') {
                                addressStr = v.address;
                            } else {
                                addressStr = [v.address.firstLineOfAddress, v.address.town, v.address.postalCode].filter(Boolean).join(', ');
                            }
                        }

                        return (
                            <div key={v.id} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                            <span style={{ backgroundColor: '#EEF2FF', color: '#4F46E5', padding: '6px 12px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                {timeString}
                                            </span>
                                            <span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>
                                                {dateString}
                                            </span>
                                        </div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                            Visit for {v.serviceUserName || 'Client'}
                                        </h4>
                                    </div>
                                    <span style={{ backgroundColor: getStatusColor(v.status) + '20', color: getStatusColor(v.status), padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600' }}>
                                        {v.status || 'Pending'}
                                    </span>
                                </div>
                                <div style={{ fontSize: '14px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    📍 {addressStr}
                                </div>
                                {v.staffNames && v.staffNames.length > 0 && (
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                        {v.staffNames.map(staff => (
                                            <span key={staff} style={{ backgroundColor: '#F3F4F6', color: '#374151', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500' }}>
                                                👤 {staff}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
            </div>
        );
    };

    const renderFeedback = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #f3f4f6', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px' }}>💬</span>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>Feedback & HR Evidence</h3>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                        Review feedback, compliments, concerns, and colleague observations for this employee.
                    </p>
                </div>

                <PaginatedList
                    title=""
                    data={feedbackRecords}
                    emptyMessage="No feedback records found."
                    renderItem={(f) => {
                        const date = f.createdAt?.toDate?.() || f.date?.toDate?.();
                        const formattedDate = date ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date) : 'N/A';
                        
                        let severityColor = '#6B7280';
                        const sev = (f.severity || '').toLowerCase();
                        if (sev === 'low' || sev === 'minor') severityColor = '#10B981';
                        if (sev === 'medium' || sev === 'moderate') severityColor = '#F59E0B';
                        if (sev === 'high' || sev === 'severe' || sev === 'critical') severityColor = '#EF4444';

                        return (
                            <div key={f.id} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                                {f.category || 'General Feedback'}
                                            </h4>
                                            {f.status && (
                                                <span style={{ backgroundColor: getStatusColor(f.status) + '20', color: getStatusColor(f.status), padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600' }}>
                                                    {f.status}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ color: '#6b7280', fontSize: '13px' }}>
                                            📅 {formattedDate} {f.authorName ? `• By ${f.authorName}` : ''}
                                        </div>
                                    </div>
                                    {f.severity && (
                                        <span style={{ backgroundColor: severityColor + '20', color: severityColor, padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', border: `1px solid ${severityColor}40` }}>
                                            Severity: {f.severity}
                                        </span>
                                    )}
                                </div>
                                
                                <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
                                    {f.description || f.details || f.comment || 'No description provided.'}
                                </div>
                            </div>
                        );
                    }}
                />
            </div>
        );
    };

    if (loading) return <div style={{ padding: '20px', color: '#666' }}>Loading Records Data...</div>;

    const subTabs = [
        { id: 'auditRecords', label: 'Audit Records' },
        { id: 'previousVisits', label: 'Previous Visits' },
        { id: 'feedback', label: 'Feedback' },
        { id: 'incidentForms', label: 'Incident Forms' }
    ];

    return (
        <div style={{ backgroundColor: '#f8f9fa', padding: '24px', borderRadius: '12px', border: '1px solid #eee' }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#333' }}>Employee Records</h2>

            {/* Sub-Tabs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
                {subTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px',
                            transition: 'all 0.2s',
                            backgroundColor: activeSubTab === tab.id ? '#1f2937' : '#f3f4f6',
                            color: activeSubTab === tab.id ? 'white' : '#4b5563',
                            boxShadow: activeSubTab === tab.id ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeSubTab === 'auditRecords' && renderAuditRecords()}
            {activeSubTab === 'previousVisits' && renderPreviousVisits()}
            {activeSubTab === 'feedback' && renderFeedback()}

            {activeSubTab === 'incidentForms' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <PaginatedList
                        title=""
                        data={incidentForms}
                        emptyMessage={
                            <div style={{ backgroundColor: 'white', padding: '32px', textAlign: 'center', borderRadius: '12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>
                                <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>📝</span>
                                No incident forms found for this employee.
                            </div>
                        }
                        renderItem={(inc) => {
                            const date = inc.createdAt?.toDate?.();
                            const formattedDate = date ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date) : 'N/A';
                            
                            return (
                                <div key={inc.id} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                                    {inc.incidentDetails?.typeOfIncident || inc.typeOfIncident || 'Incident Report'}
                                                </h4>
                                                <span style={{ backgroundColor: getStatusColor(inc.status) + '20', color: getStatusColor(inc.status), padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600' }}>
                                                    {inc.status || 'LOGGED'}
                                                </span>
                                            </div>
                                            <div style={{ color: '#6b7280', fontSize: '13px' }}>
                                                📅 {formattedDate} {inc.postedBy ? `• Reported by: ${inc.postedBy}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ backgroundColor: '#fef2f2', borderRadius: '8px', padding: '16px', fontSize: '14px', color: '#991b1b', border: '1px solid #fee2e2' }}>
                                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Details:</p>
                                        <p style={{ margin: 0 }}>{inc.incidentDetails?.descriptionOfIncident || inc.descriptionOfIncident || 'No description provided.'}</p>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            )}
        </div>
    );
}

